/**
 * Two-layer cache for AI-generated "advanced" MCQ explanations.
 *  - Layer 1: in-memory Map (fast, per session)
 *  - Layer 2: localStorage (persists across sessions)
 *
 * Bump VERSION to invalidate all stored entries (e.g. after a prompt change).
 */

const VERSION = "v1";
const KEY_PREFIX = `advExpl:${VERSION}:`;
const INDEX_KEY = `advExpl:${VERSION}:index`;
const MAX_ENTRIES = 100;

const memory = new Map<string, string>();

interface StoredEntry {
  text: string;
  generatedAt: number;
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota / private mode — ignore */
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readIndex(): string[] {
  const raw = safeGetItem(INDEX_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeIndex(ids: string[]): void {
  safeSetItem(INDEX_KEY, JSON.stringify(ids));
}

function touchIndex(mcqId: string): void {
  const ids = readIndex().filter((id) => id !== mcqId);
  ids.push(mcqId);
  // Prune oldest beyond MAX_ENTRIES
  while (ids.length > MAX_ENTRIES) {
    const oldest = ids.shift();
    if (oldest) safeRemoveItem(KEY_PREFIX + oldest);
  }
  writeIndex(ids);
}

export function getCachedAdvancedExplanation(mcqId: string): string | null {
  if (!mcqId) return null;

  const fromMem = memory.get(mcqId);
  if (fromMem) return fromMem;

  const raw = safeGetItem(KEY_PREFIX + mcqId);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredEntry;
    if (parsed && typeof parsed.text === "string" && parsed.text.length > 0) {
      memory.set(mcqId, parsed.text);
      return parsed.text;
    }
  } catch {
    /* fall through */
  }
  return null;
}

export function setCachedAdvancedExplanation(mcqId: string, text: string): void {
  if (!mcqId || !text) return;
  memory.set(mcqId, text);
  const entry: StoredEntry = { text, generatedAt: Date.now() };
  safeSetItem(KEY_PREFIX + mcqId, JSON.stringify(entry));
  touchIndex(mcqId);
}

export function clearCachedAdvancedExplanation(mcqId: string): void {
  if (!mcqId) return;
  memory.delete(mcqId);
  safeRemoveItem(KEY_PREFIX + mcqId);
  const ids = readIndex().filter((id) => id !== mcqId);
  writeIndex(ids);
}
